import { FilterItem, Heading, ScrollArea } from '@mm/components';
import { PlusCircleIcon } from '@mm/icons';
import { useAudiencesCRUDActions } from '@mm/state/actions/AudiencesCRUDActions';
import AudiencesModel from '@mm/state/model/AudiencesModel';
import { selectAudiencesPagination } from '@mm/state/selectors/messengerAdSelectors';
import { selectCurrentUserIsAdmin } from '@mm/state/selectors/userSelector';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import classNames from 'classnames';
import { debounce } from 'lodash';
import React, {
  ReactNode,
  RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { Waypoint } from 'react-waypoint';
import { SearchInput } from '../../../../../../common/components/SearchInput/SearchInput';
import Spinner from '../../../../../../common/components/Spinner';
import { MenuItem } from '../../../../../../common/components/ThreeDotMenu/ThreeDotMenu';
import { colors } from '../../../styles/colors';
import { useAudienceViewHandlers } from '../use-audience-view-handlers';
import './LeadContactsFilterByAudience.pcss';

type Props = {
  audiences: AudiencesModel[];
  className?: string;
  withMenu?: boolean | 'edit';
  listClassName?: string;
  activeAudienceId?: number;
  onSelect?: (id?: number, audience?: AudiencesModel) => void;
  onSearch?: (name?: string) => void;
  headingSlotChildren?: JSX.Element;
  isFetching?: boolean;
  onScrollEnd?: () => void;
  renderAudienceChild?: (audience: AudiencesModel) => ReactNode;
  emphasizeAllContactsForRestore?: boolean;
};

export const LeadContactsFilterByAudienceCreateButton = ({
  onClick,
}: {
  onClick?: () => void;
}) => {
  return (
    <FilterItem
      item={undefined}
      className="leads-contacts__filter-create-audience-btn"
      icon={() => <PlusCircleIcon color={colors.accentBlue} />}
      onClick={onClick}
      title="New audience"
    />
  );
};

export const LeadContactsFilterByAudience = React.forwardRef(
  (
    {
      audiences,
      className,
      activeAudienceId,
      onSelect = () => {},
      headingSlotChildren,
      onScrollEnd = () => {},
      onSearch = () => {},
      isFetching,
      listClassName,
      withMenu = true,
      renderAudienceChild,
      emphasizeAllContactsForRestore = false,
    }: Props,
    ref: RefObject<HTMLDivElement>,
  ) => {
    const pagination = useSelector(selectAudiencesPagination);

    const activeChatbotId = useSelector(activeChatbotIdSelector);
    const audiencesCRUDActions = useAudiencesCRUDActions();
    const isAdmin = useSelector(selectCurrentUserIsAdmin);

    const { onEdit, onDelete, onSync, onAllowContactsDeleteToggle, onClone } =
      useAudienceViewHandlers();

    const [search, setSearch] = useState<string>('');

    const fetchQuery = useRef(
      debounce(
        async (query: string) =>
          audiencesCRUDActions.retrieveAll(activeChatbotId, 1, 50, query),
        1000,
      ),
    ).current;

    const onSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const search = e.target.value.toLowerCase();
      setSearch(search);
      onSearch(search);
      fetchQuery(search);
    };

    // cleanup search query on unmount
    useEffect(
      () => () => {
        audiencesCRUDActions.retrieveAll(activeChatbotId, 1, 50, '', true);
      },
      [],
    );

    return (
      <div
        className={classNames(className, 'lead-contacts-filter-by-audience')}
        ref={ref}
      >
        <div className="lead-contacts-filter-by-audience__heading">
          <Heading
            className="lead-contacts-filter-by-audience__heading-text"
            text="Audiences"
            size={5}
          />
          {headingSlotChildren}
        </div>

        <SearchInput
          className="lead-contacts-filter-by-audience__search-input"
          placeholder="Search audience"
          onChange={onSearchInputChange}
          value={search}
        />

        <ScrollArea
          className={classNames(
            listClassName,
            'lead-contacts-filter-by-audience__filter-items',
          )}
        >
          {audiences.map(audience => {
            const isActive = audience.id === activeAudienceId;
            const hasAdAccount = !!audience.adAudience;
            const isDeletionEnabled = audience.deleteContacts;

            let elements: MenuItem[] = [{ name: 'Edit', value: 'edit' }];

            if (withMenu && withMenu !== 'edit') {
              if (!hasAdAccount) {
                elements.push({
                  name: 'Sync to Facebook',
                  value: 'sync',
                  requiredFeature: 'audiencesSyncContacts',
                });
              }
              elements = [
                ...elements,
                { name: 'Clone Audience', value: 'clone' },
                { name: 'Delete Audience', value: 'delete' },
                ...(isAdmin
                  ? [
                      {
                        className:
                          'lead-contacts-filter-by-audience__delete-contact-btn',
                        name: `${
                          isDeletionEnabled ? 'Disable' : 'Enable'
                        } Contacts Deletion`,
                        value: 'delete_contacts',
                      },
                    ]
                  : []),
              ];
            }

            const menuProps = {
              className: 'lead-contacts-filter-by-audience__menu',
              contentClassName:
                withMenu === 'edit'
                  ? 'lead-contacts-filter-by-audience__with-edit-menu'
                  : '',
              elements,
              selectItem: (val?: string) => {
                switch (val) {
                  case 'edit':
                    return onEdit(audience.id);
                  case 'clone':
                    return onClone(audience.id);
                  case 'delete':
                    return onDelete({
                      audienceId: audience.id,
                      audienceName: audience.name,
                      onSuccess: () => onSelect(undefined),
                    });
                  case 'sync':
                    return onSync(audience.id, audience.name);
                  case 'delete_contacts':
                    return onAllowContactsDeleteToggle(
                      audience.id,
                      !audience.deleteContacts,
                    );
                  default:
                    break;
                }
              },
            };
            const applyRestoreAllContactsTreatment =
              emphasizeAllContactsForRestore &&
              audience.name === 'All contacts';
            return (
              <FilterItem<number | undefined>
                className={classNames(
                  'lead-contacts-filter-by-audience__filter-item',
                  {
                    'lead-contacts-filter-by-audience__filter-item--active':
                      isActive,
                    'lead-contacts-filter-by-audience__filter-item--delete-allowed':
                      isDeletionEnabled,
                  },
                )}
                key={audience.id}
                title={applyRestoreAllContactsTreatment ? '' : audience.name}
                item={audience.id}
                onClick={id => onSelect(id, audience)}
                count={
                  applyRestoreAllContactsTreatment
                    ? undefined
                    : audience.contactsCount
                }
                isActive={isActive}
                menuProps={
                  withMenu && !applyRestoreAllContactsTreatment
                    ? menuProps
                    : undefined
                }
              >
                {applyRestoreAllContactsTreatment && (
                  <>
                    All website visitor data{' '}
                    <div className="lead-contacts-filter-by-audience__filter-item--recommended">
                      Recommended
                    </div>
                  </>
                )}
                {renderAudienceChild?.(audience)}
              </FilterItem>
            );
          })}

          {!isFetching && !pagination.isLastPage && (
            <Waypoint
              bottomOffset="-20px"
              onEnter={() => {
                onScrollEnd();
              }}
            />
          )}

          {isFetching && (
            <span className="lead-contacts-filter-by-audience__loader">
              <Spinner
                show
                size={14}
                position="relative"
                color={colors.accentBlue}
                backgroundColor="transparent"
              />
            </span>
          )}
        </ScrollArea>
      </div>
    );
  },
);
